// @ts-nocheck
import { takeEvery, put } from 'redux-saga/effects'
import { startSubmit, stopSubmit } from 'redux-form/immutable'
import i18next from 'i18next'

import { formatByISO, ISO_CODE, USA_ISO } from 'app/constants/phoneCountryCodes'
import isEmail from 'helpers/isEmail'
import { INVITES_FORM_SUBMIT } from 'store/entities/organizations/actionTypes'
import { adminOrganizationUsers } from 'store/entities/organizations/actions'
import { ADMIN_ORGANIZATION_USERS_INVITE_FORM as FORM_NAME } from 'app/constants/adminOrganization'
import { parseInvites } from 'helpers/parseInvites'
import { parsePhoneBeforeSend, removePhoneCodeFromPhone } from 'helpers/parsePhone'
import { prepareArray } from 'helpers/common'
// @ts-ignore
import {
  SPECIAL_CHARACTERS_TEAM_NAME,
  SPECIAL_CHARACTERS_FIRST_LAST_NAME,
  CONTAINS_SPECIAL_CHARACTERS,
} from 'app/validators/base/constants'
import {
  areSpecialCharactersInFirstLastName,
  areSpecialCharactersInTeamName,
} from 'app/validators/base/specialCharacters'
import { parseTeamsBeforeSend } from 'helpers/parseTeams'

export default function* invitesFormSaga() {
  yield takeEvery(INVITES_FORM_SUBMIT, handleInvitesFormSubmit)
}

const isDublicate = (arg: any, ind: number, arr: any, type: string) =>
  arr.some((item: any, i: number) => i > ind && item[type] === arg)

const emailDuplicated = (email: string) => i18next.t(`Email is duplicate`, { email })

const phoneDuplicated = (phone: string) => i18next.t(`Phone is duplicate`, { phone })

const phoneString = (phone: any) => i18next.t(`Invalid phone number is`, { phone })

const glueArray = (arr: any, fn: any, property: string) =>
  arr.reduce((string: string, el: any) => `${string} ${fn(el[property])}`, '')

const filterByNonEmptyEmail = (users: any) => users.filter((user: any) => user.email)

export const isInvalidPhoneNumber = (phone: string, iso: string = USA_ISO) => {
  const parsedPhone = parsePhoneBeforeSend(phone)

  const code = ISO_CODE[iso]
  const phoneWithoutCountryCode = removePhoneCodeFromPhone(parsedPhone, iso)
  const phoneWithoutCountryCodeLength = phoneWithoutCountryCode.length
  const phoneValidationFormat = formatByISO[iso]

  return (
    phoneWithoutCountryCodeLength < phoneValidationFormat.min ||
    phoneWithoutCountryCodeLength > phoneValidationFormat.max ||
    `${code}${phoneWithoutCountryCode}` !== parsedPhone
  )
}

export function* handleInvitesFormSubmit({ payload }: any) {
  try {
    const { values: immutableformData, isBulk, isInviteOnly, isDeleteAndInvite, formName } = payload
    const { users, bulkInvites, isSchedule, schedule, autoAccept, isCorrectTZatFirstLogin, usersTimezone, invitedVia } =
      immutableformData.toJS()
    const FORM_TO_HANDLE = formName || FORM_NAME

    let invites
    yield put(startSubmit(FORM_TO_HANDLE))
    if (isBulk) {
      // for this method checking teams for existing empty name will provide on backend side so that the user interface does not load

      invites = filterByNonEmptyEmail(parseInvites(bulkInvites))
      const duplicateEmails = invites.filter((item: any, i: any, arr: any) => isDublicate(item.email, i, arr, 'email'))
      if (duplicateEmails.length) {
        return yield put(
          stopSubmit(FORM_TO_HANDLE, { bulkInvites: glueArray(duplicateEmails, emailDuplicated, 'email') })
        )
      }

      const notIsEmails = invites.filter(({ email }: any) => !isEmail(email))
      if (notIsEmails.length) {
        const messageRightPart = notIsEmails.length === 1 ? ' is not an email' : ' are not emails'
        return yield put(
          stopSubmit(FORM_TO_HANDLE, {
            bulkInvites: notIsEmails.map((el: any) => el.email).join(', ') + i18next.t(messageRightPart),
          })
        )
      }
      const containSpecialCharacters = invites.filter(
        ({ firstName, lastName, teams }: any) =>
          areSpecialCharactersInFirstLastName(firstName) ||
          areSpecialCharactersInFirstLastName(lastName) ||
          (teams?.length && areSpecialCharactersInTeamName(teams.join('')))
      )
      if (containSpecialCharacters.length) {
        return yield put(
          stopSubmit(FORM_TO_HANDLE, {
            bulkInvites: containSpecialCharacters
              .map((el: any) => {
                let nameError = ''
                let teamError = ''
                const name = (el.firstName || '') + (el.firstName ? ` ${el.lastName}` : el.lastName || '')
                const teamString = el.teams?.join(' ')
                if (areSpecialCharactersInFirstLastName(name)) {
                  nameError = name + ` ${i18next.t(CONTAINS_SPECIAL_CHARACTERS)} ${SPECIAL_CHARACTERS_FIRST_LAST_NAME}`
                }
                if (areSpecialCharactersInTeamName(teamString)) {
                  teamError = `${teamString} ${i18next.t(CONTAINS_SPECIAL_CHARACTERS)} ${SPECIAL_CHARACTERS_TEAM_NAME}`
                }
                return nameError + (nameError ? '. ' : '') + teamError
              })
              .join(', '),
          })
        )
      }

      const duplicatePhones = invites.filter(
        (item: any, i: any, arr: any) => isDublicate(item.phone, i, arr, 'phone') && !!item.phone
      )
      if (duplicatePhones.length) {
        return yield put(
          stopSubmit(FORM_TO_HANDLE, { bulkInvites: glueArray(duplicatePhones, phoneDuplicated, 'phone') })
        )
      }

      const invalidPhoneNumbers = invites.filter(
        (item: any) => item.phone && isInvalidPhoneNumber(item.phone, item.phoneISO)
      )
      if (invalidPhoneNumbers.length) {
        return yield put(
          stopSubmit(FORM_TO_HANDLE, { bulkInvites: glueArray(invalidPhoneNumbers, phoneString, 'phone') })
        )
      }
      prepareArray(invites, parsePhoneBeforeSend, 'phone')
    } else {
      invites = filterByNonEmptyEmail(users)
      if (!invites.length) {
        return yield put(stopSubmit(FORM_TO_HANDLE))
      }
      prepareArray(invites, parsePhoneBeforeSend, 'phone')

      /*
         format teams values before sending to back-end only for bulk section
         from array of objects to flat array: [{name: 'a',value:'a'}] -> ['a']
      */
      prepareArray(invites, parseTeamsBeforeSend, 'teams')
    }
    yield put(
      adminOrganizationUsers.post.request({
        invites,
        isSchedule,
        schedule,
        isInviteOnly,
        isDeleteAndInvite,
        formName,
        autoAccept,
        isCorrectTZatFirstLogin,
        usersTimezone,
        invitedVia,
      })
    )
  } catch (error) {
    throw new Error(error)
  }
}
